import React, { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'

export function MissedIssueDialog({ open, result, saving, onClose, onSubmit }) {
    const checklistOptions = useMemo(() => (result?.sections || []).flatMap(section =>
        (section.items || []).map(item => ({
            id: item.id,
            label: item.label,
            section: section.title
        }))
    ), [result])
    const fileOptions = useMemo(() => result?.files || [], [result])
    const [form, setForm] = useState(emptyMissedIssue())
    const locationValid = hasParseableExactLocation(form.location)
    const canSubmit = Boolean(form.checklistItemId && form.fileId && locationValid)

    useEffect(() => {
        if (!open) setForm(emptyMissedIssue())
    }, [open])

    function update(field, value) {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogContent sx={{ p: { xs: 2.25, md: 3 }, position: 'relative' }}>
                <IconButton aria-label="Close missed issue dialog" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
                <Stack spacing={2.25}>
                    <Box sx={{ pr: 5 }}>
                        <Typography variant="overline" sx={{ color: '#9a5a16', fontWeight: 850, letterSpacing: 1 }}>
                            Improve future reviews
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#173042', fontWeight: 900 }}>
                            Report an issue the system missed
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Select the checklist item and exact location. No document text is stored.
                        </Typography>
                    </Box>

                    <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                        Do not paste confidential document contents. Page, paragraph, worksheet or cell references are sufficient.
                    </Alert>

                    <TextField
                        select
                        required
                        label="Checklist item"
                        value={form.checklistItemId}
                        onChange={(event) => update('checklistItemId', event.target.value)}
                        SelectProps={{ native: true }}
                    >
                        <option value="" />
                        {checklistOptions.map(option => (
                            <option key={option.id} value={option.id}>
                                {option.label}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        select
                        required
                        label="File"
                        value={form.fileId}
                        onChange={(event) => update('fileId', event.target.value)}
                        SelectProps={{ native: true }}
                    >
                        <option value="" />
                        {fileOptions.map(file => (
                            <option key={file.id || file.name} value={file.id || file.name}>
                                {file.name}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        required
                        label="Exact location"
                        placeholder="e.g. PDF page 8, paragraph 6 or worksheet Tax Comp, cell B12"
                        value={form.location}
                        onChange={(event) => update('location', event.target.value)}
                        inputProps={{ maxLength: 240 }}
                        error={Boolean(form.location.trim()) && !locationValid}
                        helperText={form.location.trim() && !locationValid
                            ? 'Include a page number, paragraph number, quoted heading, or worksheet and cell.'
                            : 'Examples: page 8, paragraph 6 · heading “Directors’ statement” · worksheet Tax Comp, cell B12'}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={onClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={<ReportProblemOutlinedIcon />}
                            disabled={!canSubmit || saving}
                            onClick={() => onSubmit(form)}
                        >
                            {saving ? 'Saving…' : 'Report missed issue'}
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export function QualityScorecardDialog({ open, loading, scorecard, onClose }) {
    const metrics = scorecard?.metrics || scorecard?.overview || {}
    const metricCards = [
        ['Reviews', metrics.reviews ?? metrics.reviewsScanned ?? 0],
        ['Findings reviewed', metrics.findingsReviewed ?? 0],
        ['Confirmed issues', metrics.confirmedIssues ?? 0],
        ['False positives', metrics.falsePositives ?? 0],
        ['Missed issues', metrics.missedIssues ?? 0],
        ['Exact locations', formatPercent(metrics.exactLocationRate)],
        ['Completion rate', formatPercent(metrics.completionRate)],
        ['Median review time', formatDuration(metrics.medianReviewDurationMs)]
    ]

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogContent sx={{ p: { xs: 2.25, md: 3 }, position: 'relative' }}>
                <IconButton aria-label="Close quality scorecard" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
                <Stack spacing={2.5}>
                    <Box sx={{ pr: 5 }}>
                        <Typography variant="overline" sx={{ color: '#456b7e', fontWeight: 850, letterSpacing: 1 }}>
                            Trust scorecard
                        </Typography>
                        <Typography variant="h5" sx={{ color: '#173042', fontWeight: 900 }}>
                            Review accuracy and usage
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Privacy-safe operational totals. No uploaded document text is included.
                        </Typography>
                    </Box>

                    {loading && <Alert severity="info">Loading the latest scorecard…</Alert>}
                    {!loading && !scorecard && <Alert severity="warning">No scorecard data is available yet.</Alert>}
                    {!loading && scorecard && (
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.25 }}>
                                {metricCards.map(([label, value]) => (
                                    <Paper key={label} variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                                            {label}
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: '#173042', fontWeight: 900, mt: 0.25 }}>
                                            {value}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Box>
                            {!!scorecard?.warnings?.length && (
                                <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
                                    {scorecard.warnings[0]}
                                </Alert>
                            )}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip label="Exact-location coverage" variant="outlined" />
                                <Chip label="Reviewer-confirmed outcomes" variant="outlined" />
                                <Chip label="Missed-issue learning" variant="outlined" />
                                <Chip label="Run reliability" variant="outlined" />
                            </Stack>
                        </>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export function ReplaceReviewFileDialog({ open, alert, saving, onClose, onSubmit }) {
    const [file, setFile] = useState(null)

    useEffect(() => {
        if (!open) setFile(null)
    }, [open])

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogContent sx={{ p: { xs: 2.25, md: 3 }, position: 'relative' }}>
                <IconButton aria-label="Close replacement dialog" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
                <Stack spacing={2.25}>
                    <Box sx={{ pr: 5 }}>
                        <Typography variant="overline" sx={{ color: '#456b7e', fontWeight: 850, letterSpacing: 1 }}>
                            Recheck one document
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#173042', fontWeight: 900 }}>
                            Replace {alert?.fileName || 'the affected file'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Upload the corrected working copy. Existing decisions for unaffected checklist items will be kept.
                        </Typography>
                    </Box>
                    <Button variant="outlined" component="label">
                        {file ? 'Choose another file' : 'Choose corrected file'}
                        <input
                            hidden
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            onChange={(event) => setFile(event.target.files?.[0] || null)}
                        />
                    </Button>
                    {file && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
                            <Typography sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{file.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatFileSize(file.size)}
                            </Typography>
                        </Paper>
                    )}
                    <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                        The review will rerun checks affected by this file and show what remains open.
                    </Alert>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={onClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            disabled={!file || saving}
                            onClick={() => onSubmit(file, alert)}
                        >
                            {saving ? 'Rechecking…' : 'Replace and recheck'}
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

function emptyMissedIssue() {
    return {
        checklistItemId: '',
        fileId: '',
        location: ''
    }
}

function hasParseableExactLocation(value) {
    const text = String(value || '').trim()
    const hasPage = /\bpage\s+[1-9]\d*\b/i.test(text)
    const hasParagraph = /\bparagraph\s+[1-9]\d*\b/i.test(text)
    const hasNamedHeading = /\bheading\s+["“][^"”]+["”]/i.test(text)
    const hasWorksheet = /\b(?:worksheet|sheet)\s+["“]?[^,"”]+["”]?/i.test(text)
    const hasCell = /\bcell\s+[A-Z]+[1-9]\d*\b/i.test(text)
    return hasPage || hasParagraph || hasNamedHeading || (hasWorksheet && hasCell)
}

function formatPercent(value) {
    const number = Number(value)
    if (!Number.isFinite(number)) return '—'
    return `${Math.round(number <= 1 ? number * 100 : number)}%`
}

function formatDuration(value) {
    const milliseconds = Number(value)
    if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '—'
    const minutes = Math.round(milliseconds / 60000)
    return minutes < 1 ? '<1 min' : `${minutes} min`
}

function formatFileSize(bytes) {
    const size = Number(bytes || 0)
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
}
